import Swiper from "./swiper-bundle.esm.browser.min.js";

const BREAKPOINT_TABLET = 600;
const BREAKPOINT_DESKTOP = 900;

class CustomDetailsSwiperSlider extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._resizeObserver = null;
    this._windowResizeHandler = null;
    this._initialized = false;
    this._handleClick = this._handleClick.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this.addEventListener("click", this._handleClick);
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    this._initSwiper();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    this._unbindMediaObservers();
    this.removeEventListener("click", this._handleClick);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._unbindMediaObservers();
    this._initSwiper();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    return {
      slideCount,
      spaceBetween: parseInt(this.dataset.spaceBetween || "16", 10),
      mobile: parseFloat(this.dataset.slidesPerViewMobile || "1"),
      tablet: parseFloat(this.dataset.slidesPerViewTablet || "2"),
      desktop: parseFloat(this.dataset.slidesPerViewDesktop || "3"),
      showArrows: this.dataset.showArrows === "true" && slideCount > 1,
      showPagination: this.dataset.showPagination === "true" && slideCount > 1,
      enableLoop: this.dataset.enableLoop === "true" && slideCount > 1,
      enableAutoplay: this.dataset.enableAutoplay === "true" && slideCount > 1,
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed || "5000", 10),
      pauseOnHover: this.dataset.pauseOnHover === "true",
      pauseOnInteraction: this.dataset.pauseOnInteraction !== "false",
      sectionId: this.dataset.sectionId || "",
      allowMultiple: this.dataset.allowMultipleDetailsOpen === "true",
      ctaHideLabel: this.dataset.ctaHideLabel || "Chart",
    };
  }

  _initSwiper() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return;

    this._destroySwiper();

    swiperEl.classList.add("swiper", "custom-details-slider__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    const config = this._readConfig();
    const useAutoMobileSlides = config.mobile % 1 !== 0;
    const swiperConfig = {
      grabCursor: true,
      spaceBetween: config.spaceBetween,
      slidesPerView: useAutoMobileSlides ? "auto" : config.mobile,
      watchOverflow: true,
      autoHeight: true,
      speed: 300,
      breakpoints: {
        [BREAKPOINT_TABLET]: {
          slidesPerView: config.tablet,
          spaceBetween: config.spaceBetween,
        },
        [BREAKPOINT_DESKTOP]: {
          slidesPerView: config.desktop,
          spaceBetween: config.spaceBetween,
        },
      },
    };

    if (config.enableLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows) {
      const nextEl = this.querySelector(
        `.custom-details-slider__nav-next--${config.sectionId}`,
      );
      const prevEl = this.querySelector(
        `.custom-details-slider__nav-prev--${config.sectionId}`,
      );
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination) {
      const paginationEl = this.querySelector(
        `.custom-details-slider__pagination--${config.sectionId}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.slideCount;
            return `<button type="button" class="${className}" aria-label="Slide ${num} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoplay) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    this._swiper = new Swiper(swiperEl, swiperConfig);
    this._bindMediaObservers();
    requestAnimationFrame(() => this._swiper?.update());
  }

  _bindMediaObservers() {
    this._unbindMediaObservers();

    this._resizeObserver = new ResizeObserver(() => {
      this._updateSwiperLayout();
    });

    this.querySelectorAll(".custom-details-slider__media").forEach((media) => {
      this._resizeObserver.observe(media);
    });

    this.querySelectorAll(".custom-details-slider__image").forEach((img) => {
      const syncLayout = () => this._updateSwiperLayout();
      if (img.complete) syncLayout();
      else img.addEventListener("load", syncLayout, { once: true });
    });

    this._windowResizeHandler = () => this._updateSwiperLayout();
    window.addEventListener("resize", this._windowResizeHandler, { passive: true });
  }

  _unbindMediaObservers() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    if (this._windowResizeHandler) {
      window.removeEventListener("resize", this._windowResizeHandler);
      this._windowResizeHandler = null;
    }
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._unbindMediaObservers();
  }

  _updateSwiperLayout() {
    if (this._swiper && !this._swiper.destroyed) {
      requestAnimationFrame(() => {
        this._swiper.update();
        this._swiper.updateAutoHeight(300);
      });
    }
  }

  _getLabelEl(trigger) {
    return trigger.querySelector("[data-details-label]") || trigger;
  }

  _handleClick(event) {
    const trigger = event.target.closest("[data-details-toggle]");
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    this._toggleDetails(trigger);
  }

  _toggleDetails(trigger) {
    const detailsId = trigger.getAttribute("aria-controls");
    if (!detailsId) return;

    const detailsEl = this.querySelector(`#${CSS.escape(detailsId)}`);
    const slideEl = trigger.closest("[data-swiper-slide]");
    if (!detailsEl || !slideEl) return;

    const config = this._readConfig();
    const isExpanded = trigger.getAttribute("aria-expanded") === "true";
    const labelEl = this._getLabelEl(trigger);
    const showLabel = trigger.dataset.labelShow || labelEl.textContent.trim();
    const hideLabel = trigger.dataset.labelHide || config.ctaHideLabel;

    if (isExpanded) {
      this._collapseSlide(trigger, detailsEl, slideEl, showLabel);
      return;
    }

    if (!config.allowMultiple) {
      this.querySelectorAll("[data-details-toggle][aria-expanded='true']").forEach(
        (openTrigger) => {
          if (openTrigger === trigger) return;
          const openId = openTrigger.getAttribute("aria-controls");
          const openDetails = openId
            ? this.querySelector(`#${CSS.escape(openId)}`)
            : null;
          const openSlide = openTrigger.closest("[data-swiper-slide]");
          if (openDetails && openSlide) {
            const openLabelEl = this._getLabelEl(openTrigger);
            const labelShow =
              openTrigger.dataset.labelShow || openLabelEl.textContent.trim();
            this._collapseSlide(openTrigger, openDetails, openSlide, labelShow);
          }
        },
      );
    }

    detailsEl.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    slideEl.classList.add("custom-details-slider__slide--expanded");
    labelEl.textContent = hideLabel;
    this._updateSwiperLayout();
  }

  _collapseSlide(trigger, detailsEl, slideEl, showLabel) {
    const labelEl = this._getLabelEl(trigger);
    detailsEl.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    slideEl.classList.remove("custom-details-slider__slide--expanded");
    labelEl.textContent = showLabel;
    this._updateSwiperLayout();
  }
}

if (!customElements.get("custom-details-swiper-slider")) {
  customElements.define("custom-details-swiper-slider", CustomDetailsSwiperSlider);
}
